/** @jsx jsx */

import { Fragment } from 'react';
import { css, jsx } from '@emotion/core';
import { Icon } from '@zapier/zinnia-icons';

import {
  Alert,
  Checkbox,
  Grid,
  Radio,
  Tooltip,
  TooltipFocusableTrigger,
  RadioGroup,
  Text,
  TextInput,
} from '@zapier/design-system';

import { fontFamilyMonospace } from 'app/developer-v3/components/AppBuilder/AppBuilder.styles';
import { renderMarkdown } from 'app/developer-v3/utils/markdown';
import { snakeCase } from 'lodash';

const styleIndent = css`
  padding-left: 35px;
`;

const styleContainer = css`
  /* Stop line wrapping in bad locations */
  &&&&&& code {
    word-break: unset;
  }

  &[data-need-type='code'] {
    textarea[class][class][class][class][class][class][class] {
      font-family: ${fontFamilyMonospace};
    }
  }
`;

function toScreamingSnakeCase(str: string): string {
  return snakeCase(str).toUpperCase();
}

export function createPlaceholder(str: string): string {
  return toScreamingSnakeCase(str);
}

export interface ZapierActionNeeds {
  key: string;
  type: string;
  computed?: boolean;
  prefill: string;
  label: string;
  help_text: string | null;
  list: boolean;
  required: boolean;
  placeholder: string;
}

export interface NeedsItemEditorProps {
  isEnabled: boolean;
  need: ZapierActionNeeds;
  onChange: (value: string | undefined) => void;
  value: string | undefined;
  step: 0 | 1;
}

export function NeedsItemEditor({
  isEnabled,
  need,
  onChange,
  step,
  value,
}: NeedsItemEditorProps): JSX.Element {
  const labelID = `NeedsItemEditor--${need.key}--label`;
  return (
    <div css={styleContainer} data-need-type={need.type}>
      {(() => {
        // Can add special handling if/when list prefills are supported, but for now we can at least allow one value
        // if (need.list) {
        //   // Just show the label
        //   return (
        //     <div>
        //       <NeedsItemCheckbox
        //         disabled={true}
        //         isEnabled={isEnabled}
        //         labelID={labelID}
        //         need={need}
        //         onChange={onChange}
        //         value={value}
        //       />
        //     </div>
        //   );
        // }
        switch (need.type) {
          case 'string': // Single-line text
          case 'integer':
          case 'number':
          case 'datetime':
          case 'password':
          case 'text': // Multi-line text
          case 'code': {
            return (
              <div>
                <NeedsItemCheckbox
                  disabled={false}
                  isEnabled={isEnabled}
                  labelID={labelID}
                  need={need}
                  onChange={onChange}
                  value={value}
                />
                {isEnabled && (
                  <div css={styleIndent}>
                    <TextInput
                      aria-labelledby={labelID}
                      isMultiline={need.type === 'text' || need.type === 'code'}
                      onChange={event => {
                        const elem = event.currentTarget as
                          | HTMLInputElement
                          | HTMLTextAreaElement;
                        onChange(elem.value);
                      }}
                      placeholder={createPlaceholder(
                        [step === 0 ? 'trigger' : 'action', need.key].join(' ')
                      )}
                      value={value}
                    />
                    {need.help_text && (
                      <Text>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: renderMarkdown(need.help_text || ''),
                          }}
                        />
                      </Text>
                    )}
                  </div>
                )}
              </div>
            );
          }
          case 'boolean': {
            return (
              <div>
                <NeedsItemCheckbox
                  disabled={false}
                  isEnabled={isEnabled}
                  labelID={labelID}
                  need={need}
                  onChange={onChange}
                  value={value}
                />
                {isEnabled && (
                  <div css={styleIndent}>
                    <Grid columns="repeat(2, max-content)" gap={10}>
                      <RadioGroup value={value} name={need.key}>
                        <Radio value="true">True</Radio>
                        <Radio value="false">False</Radio>
                      </RadioGroup>
                    </Grid>
                    {need.help_text && (
                      <Text>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: renderMarkdown(need.help_text || ''),
                          }}
                        />
                      </Text>
                    )}
                  </div>
                )}
              </div>
            );
          }
          case 'copy': {
            return (
              <div css={{ margin: '20px 0' }}>
                <Alert>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdown(need.help_text || ''),
                    }}
                  />
                </Alert>
              </div>
            );
          }
          default: {
            // Just show the label
            return (
              <div>
                <NeedsItemCheckbox
                  disabled={true}
                  isEnabled={isEnabled}
                  labelID={labelID}
                  need={need}
                  onChange={onChange}
                  value={value}
                />
              </div>
            );
          }
        }
      })()}
    </div>
  );
}
function NeedsItemCheckbox({
  need,
  isEnabled,
  disabled,
  labelID: _labelID,
  onChange,
  value,
}: {
  need: ZapierActionNeeds;
  isEnabled: boolean;
  disabled: boolean;
  labelID: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <Fragment>
      {disabled ? (
        <Tooltip
          allowMultiline={true}
          content="Complex field value types cannot be pre-filled"
          placement="right"
        >
          <TooltipFocusableTrigger tag="div">
            <Checkbox
              isSelected={isEnabled}
              onChange={(isSelected: boolean) => {
                if (isSelected) {
                  if (need.type === 'boolean') {
                    onChange(value ? 'true' : 'false');
                  } else {
                    onChange(value || '');
                  }
                } else {
                  onChange(undefined);
                }
              }}
            >
              <span>
                {need.required && '* '}
                {need.label || `[${need.key}]`}
              </span>
            </Checkbox>
          </TooltipFocusableTrigger>
        </Tooltip>
      ) : (
        <Checkbox
          isSelected={isEnabled}
          onChange={(isSelected: boolean) => {
            if (isSelected) {
              if (need.type === 'boolean') {
                onChange(value ? 'true' : 'false');
              } else {
                onChange(value || '');
              }
            } else {
              onChange(undefined);
            }
          }}
        >
          <span>
            {need.required && '* '}
            {need.label || `[${need.key}]`}
          </span>
        </Checkbox>
      )}
      {need.required && (
        <Tooltip
          allowMultiline={true}
          content="This is a required field when your users create this Zap. Pre-fill this field when possible to help them complete the Zap."
          placement="bottom start"
        >
          <TooltipFocusableTrigger>
            <Icon name="alertInfo" size={20} />
          </TooltipFocusableTrigger>
        </Tooltip>
      )}
    </Fragment>
  );
}
